import { useCallback, useMemo, useState } from "react";
import { Filter, StringFilter, DateFilter, DateString } from "./types";
import {
  StringFilter as GraphQlStringFilter,
  DateFilter as GraphQlDateFilter,
} from "../../__generated__/gql/graphql";
import {
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfToday,
  endOfWeek,
  endOfYear,
  endOfYesterday,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfToday,
  startOfWeek,
  startOfYear,
  startOfYesterday,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";

function getQueryFromDateString(dateString: DateString): {
  greaterThan: string;
  lessThan: string;
} {
  const now = new Date();

  if (dateString === "TODAY") {
    return {
      greaterThan: startOfToday().toISOString(),
      lessThan: endOfToday().toISOString(),
    };
  }

  if (dateString === "YESTERDAY") {
    return {
      greaterThan: startOfYesterday().toISOString(),
      lessThan: endOfYesterday().toISOString(),
    };
  }

  if (dateString === "THIS_WEEK") {
    return {
      greaterThan: startOfWeek(now).toISOString(),
      lessThan: endOfWeek(now).toISOString(),
    };
  }

  if (dateString === "LAST_WEEK") {
    const lastWeek = subWeeks(now, 1);
    return {
      greaterThan: startOfWeek(lastWeek).toISOString(),
      lessThan: endOfWeek(lastWeek).toISOString(),
    };
  }

  if (dateString === "THIS_MONTH") {
    return {
      greaterThan: startOfMonth(now).toISOString(),
      lessThan: endOfMonth(now).toISOString(),
    };
  }

  if (dateString === "LAST_MONTH") {
    const lastMonth = subMonths(now, 1);
    return {
      greaterThan: startOfMonth(lastMonth).toISOString(),
      lessThan: endOfMonth(lastMonth).toISOString(),
    };
  }

  if (dateString === "THIS_QUARTER") {
    return {
      greaterThan: startOfQuarter(now).toISOString(),
      lessThan: endOfQuarter(now).toISOString(),
    };
  }

  if (dateString === "LAST_QUARTER") {
    const lastQuarter = subQuarters(now, 1);
    return {
      greaterThan: startOfQuarter(lastQuarter).toISOString(),
      lessThan: endOfQuarter(lastQuarter).toISOString(),
    };
  }

  if (dateString === "THIS_YEAR") {
    return {
      greaterThan: startOfYear(now).toISOString(),
      lessThan: endOfYear(now).toISOString(),
    };
  }

  if (dateString === "LAST_YEAR") {
    const lastYear = subYears(now, 1);
    return {
      greaterThan: startOfYear(lastYear).toISOString(),
      lessThan: endOfYear(lastYear).toISOString(),
    };
  }

  if (dateString === "LAST_7_DAYS") {
    return {
      greaterThan: startOfDay(subDays(now, 7)).toISOString(),
      lessThan: now.toISOString(),
    };
  }

  if (dateString === "LAST_30_DAYS") {
    return {
      greaterThan: startOfDay(subDays(now, 30)).toISOString(),
      lessThan: now.toISOString(),
    };
  }

  if (dateString === "LAST_90_DAYS") {
    return {
      greaterThan: startOfDay(subDays(now, 90)).toISOString(),
      lessThan: now.toISOString(),
    };
  }

  if (dateString === "LAST_180_DAYS") {
    return {
      greaterThan: startOfDay(subDays(now, 180)).toISOString(),
      lessThan: now.toISOString(),
    };
  }

  if (dateString === "LAST_365_DAYS") {
    return {
      greaterThan: startOfDay(subDays(now, 365)).toISOString(),
      lessThan: now.toISOString(),
    };
  }

  // TODAY
  return {
    greaterThan: startOfToday().toISOString(),
    lessThan: endOfToday().toISOString(),
  };
}

export function getGraphQlStringFilter(
  filter: StringFilter,
): GraphQlStringFilter {
  return {
    contains: filter.filterType === "CONTAINS" ? filter.value : undefined,
    notContains:
      filter.filterType === "DOESNT_CONTAIN" ? filter.value : undefined,
  };
}

export function getGraphQlDateFilter(filter: DateFilter): GraphQlDateFilter {
  if (filter.filterType === "IS") {
    return getQueryFromDateString(filter.value);
  }

  if (filter.filterType === "EQUALS") {
    const date = new Date(filter.value);
    return {
      greaterThan: startOfDay(date).toISOString(),
      lessThan: endOfDay(date).toISOString(),
    };
  }

  if (filter.filterType === "BEFORE") {
    const date = new Date(filter.value);
    return {
      lessThan: startOfDay(date).toISOString(),
    };
  }

  if (filter.filterType === "AFTER") {
    const date = new Date(filter.value);
    return {
      greaterThan: endOfDay(date).toISOString(),
    };
  }

  if (filter.filterType === "BETWEEN") {
    const fromDate = new Date(filter.value.from);
    const toDate = new Date(filter.value.to);
    return {
      greaterThan: startOfDay(fromDate).toISOString(),
      lessThan: endOfDay(toDate).toISOString(),
    };
  }

  return {
    greaterThan: undefined,
    lessThan: undefined,
  };
}

export type FilterGroup = Filter[];

function useFilters({ defaultFilters }: { defaultFilters?: FilterGroup[] }) {
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>(
    defaultFilters ?? [],
  );

  const clearFilters = useCallback(() => {
    setFilterGroups([]);
  }, []);

  const deleteFilter = useCallback(
    (groupIndex: number, filterIndex: number) => {
      setFilterGroups((previousFilterGroups) => {
        const updatedFilterGroup = [
          ...previousFilterGroups[groupIndex].slice(0, filterIndex),
          ...previousFilterGroups[groupIndex].slice(filterIndex + 1),
        ];

        if (updatedFilterGroup.length === 0) {
          return [
            ...previousFilterGroups.slice(0, groupIndex),
            ...previousFilterGroups.slice(groupIndex + 1),
          ];
        }

        return [
          ...previousFilterGroups.slice(0, groupIndex),
          updatedFilterGroup,
          ...previousFilterGroups.slice(groupIndex + 1),
        ];
      });
    },
    [],
  );

  const addFilter = useCallback((filter: Filter, index: number) => {
    return setFilterGroups((previousFilterGroups) => [
      ...previousFilterGroups.slice(0, index),
      [...(previousFilterGroups[index] ?? []), filter],
      ...previousFilterGroups.slice(index + 1),
    ]);
  }, []);

  const editFilter = useCallback(
    (filter: Filter, groupIndex: number, filterIndex: number) => {
      return setFilterGroups((previousFilterGroups) => [
        ...previousFilterGroups.slice(0, groupIndex),
        [
          ...(previousFilterGroups[groupIndex] ?? []).slice(0, filterIndex),
          filter,
          ...(previousFilterGroups[groupIndex] ?? []).slice(filterIndex + 1),
        ],
        ...previousFilterGroups.slice(groupIndex + 1),
      ]);
    },
    [],
  );

  return useMemo(() => {
    return { filterGroups, addFilter, editFilter, deleteFilter, clearFilters };
  }, [addFilter, clearFilters, deleteFilter, editFilter, filterGroups]);
}

export default useFilters;
